/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext } from 'react';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { Faq } from 'ui/atoms/Faq/Faq';
import { EditorialStepsSection } from 'ui/molecules/EditorialStepsSection/EditorialStepsSection';
import { HeroSection } from 'ui/molecules/HeroSection/HeroSection';
import { CtaSection } from 'ui/organisms/CtaSection/CtaSection';
import { DifferenceSection } from 'ui/organisms/DifferenceSection/DifferenceSection';
import { NumberedFeatureCards } from 'ui/organisms/NumberedFeatureCards/NumberedFeatureCards';
import { NumberedRowsSection } from 'ui/organisms/NumberedRowsSection/NumberedRowsSection';

import { CaseStudyCard } from 'landing-pages/shared/CaseStudyCard/CaseStudyCard';
import { DeliveryChannels } from 'landing-pages/kyb-registry-verification/sections/DeliveryChannels/DeliveryChannels';
import { PerpetualKyb } from 'landing-pages/kyb-registry-verification/sections/PerpetualKyb/PerpetualKyb';
import { RegistrySourcesCard } from 'landing-pages/kyb-registry-verification/sections/RegistrySourcesCard/RegistrySourcesCard';
import { ReliabilityComparison } from 'landing-pages/kyb-registry-verification/sections/ReliabilityComparison/ReliabilityComparison';

import styles from './KybRegistryVerificationPage.module.scss';

import {
  CHANGE_FEED_ROWS,
  REGISTRY_SOURCES,
  getDeliveryGroups,
  getFaqItems,
  getReliabilityColumns,
  getSourceSteps,
  getUseCaseCards,
  getVerifyItems,
  getWhyPrimarySourceRows,
} from './KybRegistryVerificationPage.data';

export const KybRegistryVerificationPage = () => {
  const { secondaryNavigation } = useContext(ContentContext);
  const { t } = useInt();

  const freeTrialSlug = secondaryNavigation?.free_trial?.slug;

  return (
    <div className={styles.wrapper}>
      <HeroSection
        align="left"
        mediaPosition="right"
        title={
          <>
            {t('Verify any company')}
            <br />
            {t('against its government')}
            <br />
            {t('registry.')}
          </>
        }
        description={t(
          'Global Database runs KYB checks against 400+ official registries across 200+ countries. Every field in every result carries the registry name, the direct filing link, and the retrieval timestamp.',
        )}
        actions={
          <>
            {freeTrialSlug && (
              <Button type="blue" size="xxl" route={freeTrialSlug}>
                {t('Free Trial')}
              </Button>
            )}

            <Button type="dark-outline" size="xxl" href="/data-sources">
              {t('Check data sources')}
            </Button>
          </>
        }
        media={<RegistrySourcesCard items={REGISTRY_SOURCES} />}
      />

      <NumberedRowsSection
        eyebrow={t('Why primary source')}
        heading={t('A verification that holds up under audit')}
        description={
          <>
            {t(
              'When a regulator asks where a record came from, "our data provider told us" is not an answer. Every check has to trace back to the registry itself.',
            )}
          </>
        }
        rows={getWhyPrimarySourceRows(t)}
      />

      <EditorialStepsSection
        id="sources"
        theme="dark"
        background="navy"
        title={t('Every field cites its source')}
        description={t(
          'Regulators ask where a data point came from. With Global Database, the answer travels with the record itself — not in a separate report you have to reconcile.',
        )}
        steps={getSourceSteps(t)}
      />

      <CaseStudyCard
        theme="light"
        company="Allianz"
        title={t('The Allianz Italy Case Study: Growth and Compliance From a Single Data Source')}
        summary={t(
          'Allianz Italy runs KYB, AML and credit-risk checks on every client from one data source — ownership structures, financials and credit scores in a single unified feed, replacing what used to require multiple providers.',
        )}
        href="/the-allianz-italy-case-study-driving-growth-and-ensuring-compliance-with-a-single-data-source"
      />

      <NumberedFeatureCards
        eyebrow={t('Use cases')}
        title={t('Where teams put primary-source data to work')}
        description={t(
          'One verified source of company truth, applied across the workflows that depend on knowing who a business really is.',
        )}
        items={getUseCaseCards(t)}
        columns={3}
      />

      <DifferenceSection
        align="left"
        extra={t('What you verify')}
        title={t('The complete legal identity of a business')}
        description={t(
          'One check opens the full picture — from the basic registry record through to officers, ownership, financials, and group structure.',
        )}
        items={getVerifyItems(t)}
      />

      <ReliabilityComparison
        eyebrow={t('Reliability')}
        title={
          <>
            {t('Registries go down.')}
            <br />
            {t('Your onboarding keeps running.')}
          </>
        }
        description={t(
          'Both routes end at the same register. The difference is whether that register has to be awake when you ask, and what you are left holding afterwards. Every field we return carries the moment it was last read, so you can see how current it is.',
        )}
        columns={getReliabilityColumns(t)}
      />

      <PerpetualKyb
        eyebrow={t('Perpetual KYB')}
        title={t('A file you cleared last year is a claim about last year')}
        description={t(
          'Move any verified company into perpetual monitoring. We re-read its registry record daily and push the change to you — status, officers, registered address, ownership, and new filings — so a review is triggered by the register, not by the calendar.',
        )}
        ctaLabel={t('See perpetual monitoring')}
        ctaHref="/book-a-demo"
        rows={CHANGE_FEED_ROWS}
      />

      <DeliveryChannels
        eyebrow={t('Delivery')}
        title={t('Run checks where your team already works')}
        groups={getDeliveryGroups(t)}
      />

      <CtaSection
        spotlight
        theme="dark"
        title={t('Verify your next counterparty from the primary record')}
        description={t('See a live KYB check on a company you choose, in any of 200+ jurisdictions.')}
        actions={
          <Button type="blue" size="xl" href="/book-a-demo">
            {t('Book a demo')}
          </Button>
        }
      />

      <Faq title={t('Questions, answered')} data={getFaqItems(t)} />
    </div>
  );
};
